import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useChatWidgetEffectiveOpen from './use-chat-widget-effective-open';
import useChatWidgetEnabled from './use-chat-widget-enabled';
import useChatWidgetLayout from './use-chat-widget-layout';
import useChatWidgetLockedFullscreen from './use-chat-widget-locked-fullscreen';
import useChatWidgetSidebarWidth from './use-chat-widget-sidebar-width';
import useInvestigationDock from './use-investigation-dock';

// Owns `--chat-widget-width` on the persistent layout row (not the slot, which
// unmounts on close) so the content card can animate back to full width.
const useChatWidgetSlotWidth = () => {
  const $ = _c(10);
  const rowRef = useRef(null);
  const enabled = useChatWidgetEnabled();
  const isMobile = useMobileLayout();
  const effectiveOpen = useChatWidgetEffectiveOpen();
  const {
    isSidebar
  } = useChatWidgetLayout();
  const {
    width
  } = useChatWidgetSidebarWidth();
  const lockedFullscreen = useChatWidgetLockedFullscreen();
  const lockedSidebar = useInvestigationDock();
  const docked = !isMobile && (isSidebar || lockedSidebar) && !lockedFullscreen;
  const active = enabled && effectiveOpen && docked;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = px => rowRef.current?.style.setProperty("--chat-widget-width", `${px}px`);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const writeWidthVar = t0;
  const prevDockedRef = useRef(docked);
  const paintedWidthRef = useRef(null);
  let t1;
  let t2;
  if ($[1] !== active || $[2] !== docked || $[3] !== width) {
    t1 = () => {
      const next = active ? width : 0;
      const el = rowRef.current;
      if (!el) {
        return;
      }
      const painted = paintedWidthRef.current;
      const modeChanged = prevDockedRef.current !== docked;
      prevDockedRef.current = docked;
      writeWidthVar(next);
      if (painted === null || painted === next || modeChanged) {
        el.removeAttribute("data-animate-width");
      } else {
        el.setAttribute("data-animate-width", "true");
      }
    };
    t2 = [active, width, docked];
    $[1] = active;
    $[2] = docked;
    $[3] = width;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
  const paintedWidth = active ? width : 0;
  let t3;
  let t4;
  if ($[6] !== paintedWidth) {
    t3 = () => {
      const id = requestAnimationFrame(() => {
        paintedWidthRef.current = paintedWidth;
      });
      return () => cancelAnimationFrame(id);
    };
    t4 = [paintedWidth];
    $[6] = paintedWidth;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    const setResizing = resizing => {
      const el_0 = rowRef.current;
      if (!el_0) {
        return;
      }
      if (resizing) {
        el_0.setAttribute("data-resizing", "true");
      } else {
        el_0.removeAttribute("data-resizing");
      }
    };
    t5 = {
      rowRef,
      writeWidthVar,
      setResizing
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export default useChatWidgetSlotWidth;